/**
 * The commercial terms, stated once.
 *
 * Confirmed by Kabex on 2026-10-21, and they REPLACE the flat "50% of the CIF
 * price" the site published until then (A12, 2026-09-17). Three things changed
 * at once, which is why they are gathered here instead of being edited into
 * thirty sentences across thirteen files:
 *
 *   1. An import opens at **30% or 50%** of CIF, not always half, and only
 *      after the client has reviewed the quotation and given a clear go-ahead.
 *   2. The balance may be paid **in instalments** rather than in one transfer.
 *   3. The deadline is no longer tied to **Mombasa** — see `BALANCE_DEADLINE`.
 *
 * WHAT THIS MODULE IS FOR. The old wording was a literal string in every band
 * that mentioned money, so a change to the deal meant finding every one of
 * them and the site spent a day telling two different stories. Everything that
 * states a term now reads it from here. `content/site.ts` and
 * `content/services.ts` interpolate these constants rather than restating the
 * figures, so the next change to the deal is one edit in this file.
 *
 * WHAT MUST NOT BE ADDED HERE. Kabex was explicit on 2026-10-21 that the site
 * does **not** publish the rule deciding whether a given car opens at 30% or
 * at 50% — the quotation does that, in writing, per car. Do not infer a rule
 * from a pattern in the orders table and publish it; "which one depends on the
 * car" is the claim, and it is deliberately the whole of it.
 *
 * Likewise `installments` carries NO figures: no minimum, no duration, no
 * instalment count. The internal Installment Plans feature has all of them and
 * they are commercial terms per client, so a number on a public page would be
 * a promise Kabex has not made. The public claim is the *mechanics* — written
 * plan, receipt for every payment, statement on request — because that is what
 * answers the objection a visitor actually has, which is not "how much" but
 * "will my money disappear".
 */

import { type ContentItem } from './contentTypes';

/**
 * The two shares an import can open at, in the order they are always said.
 *
 * Ascending, and that ordering is the marketing decision: "30% or 50%" leads
 * with the lower barrier, which is the reason Kabex gave us the term in the
 * first place. Never render this reversed.
 */
export const DEPOSIT_SHARES = ['30%', '50%'] as const;

/** `30% or 50%` — the shares as one phrase, built from the pair above. */
export const DEPOSIT_RANGE = DEPOSIT_SHARES.join(' or ');

/**
 * When the balance falls due, with no port named.
 *
 * Kabex ships through Mombasa, Dar es Salaam and Zanzibar among others
 * (already stated on the clearing-and-forwarding page), so "two weeks before
 * the vessel docks in Mombasa" was a term quietly narrowed to one port — and
 * for a buyer whose car is routed through Dar es Salaam it was simply the
 * wrong date. "Reaches port" is true of every route.
 *
 * The two weeks are unchanged (A12) and are the operationally load-bearing
 * part: the duty cannot be assessed and the car cannot be released until the
 * balance has cleared, so a deadline at docking is a car accruing charges.
 *
 * Mombasa is still named across the site wherever it is a FACT rather than a
 * deadline — the CIF basis the calculators price from, the road-transport
 * tables, the clearing route. Do not genericise those; the calculators would
 * become wrong.
 */
export const BALANCE_DEADLINE = 'two weeks before your car reaches port';

/** `before your car reaches port` — for sentences that carry their own timing. */
export const PORT_ARRIVAL = 'before your car reaches port';

/**
 * The deposit term, in the four lengths the site needs it.
 *
 * Four rather than one because a chip on a six-step spine, a bento tile, a
 * fact row and a page of body copy have genuinely different room, and the
 * alternative — one string, truncated by whoever draws it — is how the term
 * ends up meaning something slightly different in each band.
 */
export const deposit = {
  /** A chip or fact value. Shortest form that still carries both figures. */
  short: `${DEPOSIT_RANGE} of CIF`,
  /** A fact row or list item. */
  medium: `After your go-ahead, ${DEPOSIT_RANGE} of the CIF price starts your import`,
  /** A section heading. Leads on what the visitor keeps, not what they owe. */
  headline: `Start with ${DEPOSIT_RANGE}, not the whole price`,
  /**
   * The full claim, including the one thing we are allowed to say about which
   * share applies. The "in writing, before you pay anything" clause is doing
   * the real work: without it, "depends on the car" reads as room to move the
   * number after a buyer is committed.
   */
  body: `You do not need the full amount to begin. An import opens at ${DEPOSIT_RANGE} of the CIF price — which of the two depends on the car, and your quotation states it in writing before you pay anything. Kabex begins only after you have reviewed the quotation and given a clear go-ahead.`,
  /** Said wherever the body copy above has already explained the pair. */
  ruleNote: 'Which share applies depends on the car. Your quotation confirms it.',
} as const;

/**
 * The balance, in the same four lengths.
 *
 * `body` is the one sentence on the site that joins the two new terms
 * together, and it is the most commercially useful sentence we have: the
 * deposit lowers the entry barrier and the instalments remove the cliff a
 * fortnight before arrival. Said separately they are two features; said
 * together they are an answer to "I cannot afford this yet".
 */
export const balance = {
  short: 'The rest, in instalments if you like',
  medium: `The balance, settled ${BALANCE_DEADLINE}`,
  headline: 'Clear the balance at your own pace',
  body: `The rest is due ${BALANCE_DEADLINE}, and it does not have to arrive in one transfer. Pay it down in instalments across the weeks your car is at sea, so the balance is already settled by the time it lands.`,
  /**
   * The instalment half of `body`, on its own.
   *
   * It is a separate field rather than a sentence sliced back out of `body` at
   * the call site, because the bands that need only this half need it often:
   * anywhere the deadline has already been stated, repeating it is noise, and
   * the option not to pay in one go is the part that changes a mind.
   */
  instalmentOption:
    'It does not have to arrive in one transfer. Pay it down in instalments across the weeks your car is at sea, so it is already settled by the time the car lands.',
} as const;

/**
 * Instalments. Mechanics only — see the module note on why there are no
 * figures here, and do not add any.
 */
export const installments = {
  eyebrow: 'Instalment plans',
  headline: 'A clear plan. A receipt every time.',
  /**
   * "A written plan, not an understanding" is the whole pitch in six words.
   * Informal instalment arrangements are normal in this market and they are
   * exactly what a buyer sending millions of shillings to a dealer is afraid
   * of; the distinction Kabex can draw is that theirs is documented.
   */
  body: 'Paying over time at Kabex Motors follows a written plan agreed with you. After each payment is confirmed, Kabex emails you an e-receipt. You can request an up-to-date statement of what you have paid and what remains at any time. You may request a refund at any time; each request is reviewed, and the approved amount is confirmed before payout.',
  /** The proof points, ordered around the buyer’s practical concerns. */
  proof: [
    {
      icon: 'quote',
      title: 'Terms agreed first',
      text: 'Your payment plan is documented before you start, including the agreed amounts and timing.',
    },
    {
      icon: 'verified',
      title: 'E-receipts by email',
      text: 'Every payment is recorded against your car and an e-receipt is emailed to you.',
    },
    {
      icon: 'updates',
      title: 'Your balance, on request',
      text: 'Ask for a statement at any time to see what you have paid and what remains.',
    },
    {
      icon: 'verified',
      title: 'You can request a refund',
      text: 'You may ask for a refund at any time. Kabex reviews the request and confirms the approved amount before payout.',
    },
  ] satisfies ContentItem[],
} as const;

/**
 * Registration in the buyer's own name.
 *
 * Not a new claim — the vehicle-registration service has said it since launch
 * — but it was buried on a service page three clicks from the home page, and
 * Kabex named it on 2026-10-21 as one of the three things to lead with. It is
 * the strongest of the three against a bond or a lot, because it is the one a
 * competitor physically cannot match: a car that sat on someone else's floor
 * was already registered to them.
 */
export const ownership = {
  eyebrow: 'Owned outright',
  headline: 'The logbook comes in your name',
  short: 'Logbook in your name',
  body: 'Not a dealership’s name, not ours, and not transferred to you afterwards. Your car is registered to you the first time it is registered at all — so there is no previous owner on the logbook, and no transfer to pay for that you should never have paid for.',
} as const;

/**
 * The three terms as one list, for the bands that show all of them together.
 *
 * Declared here rather than assembled at each call site so the home page, the
 * how-it-works page and the payment-support page cannot show two of the three
 * and silently drop the one a given visitor needed.
 */
export const paymentHighlights: readonly ContentItem[] = [
  { icon: 'payment', title: deposit.medium, text: deposit.ruleNote },
  { icon: 'calendar', title: balance.headline, text: balance.body },
  { icon: 'registration', title: ownership.headline, text: ownership.body },
];

/**
 * The reassurance that every band stating a figure has to carry.
 *
 * Kept beside the figures, not in the footer: the moment a visitor reads an
 * amount is the moment they wonder where it is going, and the sentence is
 * worth nothing a screen and a half below the number that prompted it.
 */
export const paymentSafetyNote =
  'Every payment is made to the Kabex Motors company account against an invoice, and receipted. Nothing is payable through this website.';
